import { WithStringValues } from "common/utils/types";

import {
  ProductLineListSearch,
  ProductDealsSearch,
  ProductSearchSearch,
  BrowseSearch,
  IncentiveItemsSearch,
  RebateItemsSearch,
  ProductLineAvailabilitySearch,
  ItemPricingSearch,
} from "../types";

/** Filter-option scopes, prefixed by kind so a per-entity scope cannot collide with a page's. */
export type FilterOptionScope = `page:${string}` | `collection:${string}`;

export const PRODUCT_SEARCH_FILTER_SCOPE = "page:search";
export const PRODUCT_DEALS_FILTER_SCOPE = "page:deals";
export const collectionFilterScope = (slug: string): FilterOptionScope => `collection:${slug}`;

export const urls = {
  productLineSearch: () => "/api/client/v1/search/product-lines/",
  productSearch: () => "/api/client/v1/search/products/",
  productSearchV2: () => "/api/client/v1/search/products-v2/",
  productDealsSearch: () => "/api/client/v1/search/products/deals/",
  productLine: (id: number) => `/api/client/v1/catalog/product-lines/${id}/`,
  product: (id: number) => `/api/client/v1/catalog/products/${id}/`,
  browse: () => "/api/client/v1/search/browse/",
  carousels: () => "/api/client/v1/search/carousels/",
  category: (id: number) => `/api/client/v1/search/categories/${id}/`,
  incentive: (id: number) => `/api/client/v1/promotions/incentives/${id}/`,
  incentiveItems: (id: number) => `/api/client/v1/promotions/incentives/${id}/items/`,
  rebate: (id: number) => `/api/client/v1/promotions/rebates/${id}/`,
  rebateItems: (id: number) => `/api/client/v1/promotions/rebates/${id}/items/`,
  productLineAvailability: (id: number) =>
    `/api/client/v1/catalog/product-lines/${id}/availability/`,
  itemAddToCartPricing: (id: number) => `/api/client/v1/catalog/items/${id}/pricing/add-to-cart/`,
};

export const keys = {
  all: () => ["products-all"],
  // search
  search: () => [...keys.all(), "search"],
  browse: () => [...keys.search(), "browse"],
  browseFilter: (search: WithStringValues<BrowseSearch>) => [...keys.browse(), search],
  carousels: () => [...keys.search(), "carousels"],
  carouselsFilter: (search: WithStringValues<BrowseSearch>) => [...keys.carousels(), search],
  productLineSearches: () => [...keys.search(), "product-lines"],
  productLineSearch: (search: WithStringValues<ProductLineListSearch>) => [
    ...keys.productLineSearches(),
    search,
  ],
  productSearches: () => [...keys.search(), "products"],
  productSearch: (search: WithStringValues<ProductSearchSearch>, v2 = false) => [
    ...keys.productSearches(),
    v2 ? "v2" : "v1",
    search,
  ],
  productDealsSearches: () => [...keys.search(), "product-deals"],
  productDealsSearch: (search: WithStringValues<ProductDealsSearch>) => [
    ...keys.productDealsSearches(),
    search,
  ],

  // categories
  categories: () => [...keys.search(), "categories"],
  categoryDetails: () => [...keys.categories(), "detail"],
  categoryDetail: (id: number) => [...keys.categories(), id],

  // product lines
  productLines: () => [...keys.all(), "product-lines"],
  productLineDetails: () => [...keys.productLines(), "detail"],
  productLineDetail: (id: number) => [...keys.productLineDetails(), id],
  productLineAvailabilities: () => [...keys.productLines(), "availabilities"],
  productLineDetailAvailability: (
    id: number,
    search: WithStringValues<ProductLineAvailabilitySearch>,
  ) => [...keys.productLineAvailabilities(), id, search],

  // products (V2 endpoint)
  products: () => [...keys.all(), "products"],
  productDetails: () => [...keys.products(), "detail"],
  productDetail: (id: number) => [...keys.productDetails(), id],

  // item add-to-cart pricing
  itemPrices: (id: number, params: ItemPricingSearch) => [
    ...keys.all(),
    "add-to-cart-prices",
    id,
    params,
  ],

  // state
  state: () => [...keys.all(), "state"],
  /**
   * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
   * or a separate endpoint for getting the filters
   * https://app.shortcut.com/vetcove/story/32581/
   * */
  productFilterOptionState: (scope: FilterOptionScope) => [
    ...keys.state(),
    "initial-product-filter-options",
    scope,
  ],
  /**
   * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
   * or a separate endpoint for getting the filters
   * https://app.shortcut.com/vetcove/story/32581/
   * */
  rebateProductFilterOptionState: () => [...keys.state(), "initial-rebate-product-filter-options"],
  /**
  /**
   * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
   * or a separate endpoint for getting the filters
   * https://app.shortcut.com/vetcove/story/32581/
   * */
  incentiveProductFilterOptionState: () => [
    ...keys.state(),
    "initial-incentive-product-filter-options",
  ],
  /**
  /**
   * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
   * or a separate endpoint for getting the filters
   * https://app.shortcut.com/vetcove/story/32581/
   * */
  categoryProductLineFilterOptionState: (categoryIds: number[]) => [
    ...keys.state(),
    "initial-product-line-filter-options",
    categoryIds,
  ],

  // incentives
  incentives: () => [...keys.all(), "incentives"],
  incentiveDetails: () => [...keys.incentives(), "detail"],
  incentiveDetail: (id: number) => [...keys.incentiveDetails(), id],
  incentiveItems: () => [...keys.incentives(), "items"],
  incentiveItemLists: () => [...keys.incentiveItems(), "list"],
  incentiveItemList: (id: number, search: IncentiveItemsSearch) => [
    ...keys.incentiveItemLists(),
    id,
    search,
  ],

  // rebates
  rebates: () => [...keys.all(), "rebates"],
  rebateDetails: () => [...keys.rebates(), "detail"],
  rebateDetail: (id: number) => [...keys.rebateDetails(), id],
  rebateItems: () => [...keys.rebates(), "items"],
  rebateItemLists: () => [...keys.rebateItems(), "list"],
  rebateItemList: (id: number, search: RebateItemsSearch) => [
    ...keys.rebateItemLists(),
    id,
    search,
  ],
};
